/**
 * YAML configuration singleton for astrology settings.
 *
 * Python loads astro_calculations/astrology/astrology_config.yaml at runtime
 * (AstrologyConfig.load → _parse_config). A static browser deploy has no YAML
 * loader, so the values the engine actually consumes are translated to a TS
 * literal here. The live port closure reads exactly:
 *   - cfg.rulerships  (calculate_chart → compute_shadbala_lite / compute_functional_roles)
 *   - cfg.node_type   (compute_node_positions: mean vs true node)
 *   - cfg.graha_drishti (drishti helpers)
 * The remaining parsed attributes (_parse_config's transit weights, dasha
 * influences, profection/house influences, event detection, metric tables,
 * tooltips) belong to the deprecated _get_astro_predictions path that the live
 * lib/ engine never calls; they are kept as empty placeholders so the object
 * shape matches the Python class without dragging 1300 YAML lines into the
 * bundle. (The LEGACY scorer's separate static_astrology_config.yaml IS fully
 * translated — see static_config_data.ts.)
 */

export interface AstrologyConfig {
  rulerships: Record<number, string>;
  // --- Backward-compat (legacy angle/orb config; YAML "global" has no such
  // sections, so Python parses these to empty containers) ---
  aspects: Record<number, number>;
  soft_aspects: Set<number>;
  hard_aspects: Set<number>;
  orbs: Record<string, number>;
  // --- end backward-compat ---
  node_type: string;
  graha_drishti: Record<string, number[]>;
  transit_weights: Record<string, number>;
  transit_multipliers: Record<string, number>;
  dasha_offsets: Record<string, Record<string, number>>;
  divisional_dasha_offsets: Record<string, Record<string, number>>;
  antardasha_multiplier: number;
  profection_emphasis: Record<string, Record<string, number>>;
  divisional_profection: Record<string, Record<string, number>>;
  house_influences: Record<string, Record<string, number>>;
  divisional_house: Record<string, Record<string, number>>;
  strength_modifier_range: number;
  event_detection: Record<string, any>;
  metrics_config: Record<string, any>;
  divisional_config: Record<string, any>;
  tooltips: Record<string, any>;
}

/** Values transcribed from astrology_config.yaml (rulerships / global.nodes / global.graha_drishti). */
const _CONFIG: AstrologyConfig = {
  // Rulerships (essential for Vedic)
  rulerships: {
    0: "mars", // Aries
    1: "venus", // Taurus
    2: "mercury", // Gemini
    3: "moon", // Cancer
    4: "sun", // Leo
    5: "mercury", // Virgo
    6: "venus", // Libra
    7: "mars", // Scorpio (traditional)
    8: "jupiter", // Sagittarius
    9: "saturn", // Capricorn
    10: "saturn", // Aquarius (traditional)
    11: "jupiter", // Pisces
  },
  aspects: {},
  soft_aspects: new Set<number>(),
  hard_aspects: new Set<number>(),
  orbs: {},
  // Node type (global.nodes.type: mean)
  node_type: "mean",
  // Graha Drishti (Vedic planetary aspects by house distance). Special full aspects belong to
  // Mars/Jupiter/Saturn only (02_planetary_aspects.md / 05_aspects_drishti.md, BPHS Ch.26); the
  // nodes get NO special aspects — they aspect the 7th only, like the default. (This map is not
  // consumed by the live drishti path, which uses get_drishti_strength; kept consistent anyway.)
  graha_drishti: {
    default: [7], // All planets — incl. Rahu/Ketu — aspect the 7th
    mars: [4, 7, 8],
    jupiter: [5, 7, 9],
    saturn: [3, 7, 10],
  },
  transit_weights: {},
  transit_multipliers: {},
  dasha_offsets: {},
  divisional_dasha_offsets: {},
  antardasha_multiplier: 0.5,
  profection_emphasis: {},
  divisional_profection: {},
  house_influences: {},
  divisional_house: {},
  strength_modifier_range: 0.3,
  event_detection: {},
  metrics_config: {},
  divisional_config: {},
  tooltips: {},
};

/** Get or create the config singleton. */
export function get_config(): AstrologyConfig {
  return _CONFIG;
}
